import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getSpecificErrorMessage } from '../utils/errorHelpers';

export default function LoginPage() {
  const { isAuthenticated, loading, login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionMessage, setSessionMessage] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Check for session expiration message and OAuth errors from query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    const errorParam = params.get('error');
    
    if (message) {
      setSessionMessage(decodeURIComponent(message));
    }
    
    // Handle OAuth error parameters
    if (errorParam) {
      switch (errorParam) {
        case 'not_registered':
          setAuthError('Your email is not registered. Please contact an administrator to grant you access.');
          break;
        case 'account_deactivated':
          setAuthError('Your account has been deactivated. Please contact an administrator.');
          break;
        case 'pending_approval':
          setAuthError('Your account is pending approval. Please wait for an administrator to approve your access.');
          break;
        case 'unauthorized_email':
          setAuthError('Your email domain is not authorized to access this system.');
          break;
        default:
          setAuthError(`Login failed: ${errorParam}. Please try again or contact support.`);
      }
      
      // Clear the error param from URL
      params.delete('error');
      const newUrl = params.toString() ? `${location.pathname}?${params}` : location.pathname;
      navigate(newUrl, { replace: true });
    }
    
    // Clear any auth errors when component mounts
    if (error) {
      clearError();
    }
  }, [location, error, clearError, navigate]);
  
  // Redirect to home if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('Login button clicked');
    try {
      await login();
      // Redirect happens in the auth provider via window.location
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = getSpecificErrorMessage(err, 'log in', {
        resourceType: 'User'
      });
      setAuthError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5', 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 6, 
          maxWidth: 400, 
          mx: 'auto',
          textAlign: 'center',
          borderRadius: 3
        }}
      >
        <Box
          component="img"
          src="https://cdn.prod.website-files.com/5cec2e0838469c2603af4fce/67131cbdd54753b27c1f5193_Blades%20Audio%20Video%20Security%20Logo.png"
          alt="Blades Audio Video Security Logo"
          sx={{ 
            maxWidth: '250px',
            height: 'auto',
            mb: 4,
            display: 'block',
            mx: 'auto',
            // Dark background so white text is visible
            backgroundColor: '#333',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />

        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            mb: 3, 
            fontWeight: 300,
            color: '#333'
          }}
        >
          Inventory System
        </Typography>

        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            color: 'text.secondary',
            lineHeight: 1.6
          }}
        >
          Sign in with your Google account to access the inventory system
        </Typography>

        {(error || authError) && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => {
            if (error) clearError();
            if (authError) setAuthError(null);
          }}>
            {error || authError}
          </Alert>
        )}
        
        {sessionMessage && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSessionMessage(null)}>
            {sessionMessage}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          component="a"
          href="/api/auth/google/login"
          sx={{ 
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: '#000000',
            '&:hover': {
              backgroundColor: '#FF0000',
            }
          }}
        >
          Sign in with Google
        </Button>
      </Paper>
    </Box>
  );
}